import { Button } from '@creditornot/cb-button';
import { DateRangePickerInput } from '@creditornot/cb-date-picker';
import { Download } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { splitEvery } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { DateParam, StringParam, useQueryParams } from 'use-query-params';

import { readCompletedPurchases, readRejectedPurchases } from 'apis/restaurant-api/merchant-admin/purchases/api';
import { Purchase } from 'apis/restaurant-api/merchant-admin/purchases/types';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { downloadEncodedCsv } from 'common/utils';
import getErrorContent from 'common/utils/error';
import { getDateRangeDelta } from 'common/utils/time/formatting';
import { useDownloadBrandPurchasedDataV2 } from 'modules/enterprise/hooks/useDownloadBrandPurchasedData';
import { purchaseCsvDownloadError, purchaseCsvDownloadView } from 'modules/enterprise/telemetry/views';
import { toByDateCSV } from 'modules/purchases/utils';

import VenuesTable from './VenuesTable';
import { useMerchantContext } from '../MerchantContext';
import { PreDefinedPeriod } from '../types';
import { getDisplayRangeFromPreDefinedPeriod, getIntervalForThePeriod, isValidRangeQuery } from '../utils';

const MAX_CONCURRENT_REQUESTS = 5;
const MAX_PURCHASES_PER_REQUEST = 500;
const DEFAULT_PERIOD: PreDefinedPeriod = 'yesterday';

const Root = styled.div`
  background-color: ${cssThemedValue('surfaceMain')};
  color: ${cssThemedValue('textPrimary')};
  display: flex;
  width: 100%;
  height: 100%;
`;

const Sidebar = styled.div`
  flex-shrink: 0;
  width: 320px;
  padding: 24px;
  border-inline-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const DateRangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-start: 16px;
  margin-inline-start: -8px;
  margin-inline-end: -8px;
`;

const RightContent = styled.div`
  overflow: auto;
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
  padding: 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const EmptyWrapper = styled.div`
  margin-block-start: 32px;
  flex: 1;
`;

const periodTranslationMap: Record<PreDefinedPeriod, string> = {
  yesterday: 'purchases.labels.yesterday',
  'this-week': 'purchases.labels.this-week',
  'last-week': 'purchases.labels.last-week',
  'this-month': 'purchases.labels.this-month',
  'last-month': 'purchases.labels.last-month',
};

const recursiveReadPurchases = async (
  status: 'delivered' | 'rejected',
  payload: {
    venueId: string;
    startTime: string;
    endTime: string;
    limit: number;
    cursor?: string;
  },
  previousPurchases: Purchase[] = [],
): Promise<Purchase[]> => {
  const res = status === 'delivered' ? await readCompletedPurchases(payload) : await readRejectedPurchases(payload);
  if (!res.metadata?.next) {
    return [...previousPurchases, ...res.data.purchases];
  }
  const nextPurchases = await recursiveReadPurchases(
    status,
    { ...payload, cursor: res.metadata.next },
    res.data.purchases,
  );
  return [...previousPurchases, ...nextPurchases];
};

const MerchantPurchaseData = () => {
  const startTime = performance.now();
  const intl = useIntl();

  const { merchant, merchantVenues: venues } = useMerchantContext();
  const merchantId = merchant.itemid.$oid;

  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>([]);

  const [dateRangeQuery, setDateRangeQuery] = useQueryParams({
    period: StringParam,
    from: DateParam,
    to: DateParam,
  });

  useEffect(() => {
    if (!isValidRangeQuery(dateRangeQuery)) {
      setDateRangeQuery({ period: DEFAULT_PERIOD, from: undefined, to: undefined });
    }
  }, [dateRangeQuery, setDateRangeQuery]);

  const downloadPeriod = useMemo(() => {
    if (!isValidRangeQuery(dateRangeQuery)) {
      return DEFAULT_PERIOD;
    }
    return dateRangeQuery.period;
  }, [dateRangeQuery]);

  const dateRange = useMemo(() => {
    if (!isValidRangeQuery(dateRangeQuery)) {
      return getDisplayRangeFromPreDefinedPeriod(DEFAULT_PERIOD);
    }
    if (dateRangeQuery.period === 'custom') {
      return { from: dateRangeQuery.from, to: dateRangeQuery.to };
    }
    return getDisplayRangeFromPreDefinedPeriod(dateRangeQuery.period);
  }, [dateRangeQuery]);

  useEffect(() => {
    if (venues) {
      setSelectedVenueIds(venues.filter(venue => venue.status === 'VENUE_PUBLISHED').map(venue => venue.id));
    }
  }, [venues]);
  const merchantName = localize(merchant.name, intl.locale);
  const { isLoading: isDownloading } = useMutation(
    async () => {
      if (!venues) {
        throw new Error('No venues for downloading data! This should not happen!');
      }
      const allPurchases: Purchase[] = [];
      const venuesToDownload = venues.filter(venue => selectedVenueIds.includes(venue.id));
      const [{ timezone }] = venuesToDownload;
      const interval = getIntervalForThePeriod(downloadPeriod, timezone, dateRange);

      const venueChunks = splitEvery(MAX_CONCURRENT_REQUESTS, venuesToDownload);
      for (const venueChunk of venueChunks) {
        const deliveredResultChunk = await Promise.all(
          venueChunk.map(venue =>
            recursiveReadPurchases('delivered', {
              venueId: venue.id,
              startTime: interval.start.toISO(),
              endTime: interval.end.toISO(),
              limit: MAX_PURCHASES_PER_REQUEST,
            }),
          ),
        );
        deliveredResultChunk.map(res => allPurchases.push(...res));
        const rejectedResultChunk = await Promise.all(
          venueChunk.map(venue =>
            recursiveReadPurchases('rejected', {
              venueId: venue.id,
              startTime: interval.start.toISO(),
              endTime: interval.end.toISO(),
              limit: MAX_PURCHASES_PER_REQUEST,
            }),
          ),
        );
        rejectedResultChunk.map(res => allPurchases.push(...res));
      }
      const localizedPurchases: Purchase[] = allPurchases.map(purchase => {
        const venue = venuesToDownload.find(venue => venue.id === purchase.restaurant.id);
        return {
          ...purchase,
          restaurant: {
            ...purchase.restaurant,
            name: venue ? localize(venue.name, intl.locale) : purchase.restaurant.name,
          },
        };
      });
      const csvData = toByDateCSV(localizedPurchases, venuesToDownload[0].timezone, intl);
      const filename = `${merchantName}-purchases-${downloadPeriod}-${DateTime.local().toISODate()}.csv`;
      downloadEncodedCsv(csvData, 'text/csv', filename, merchant.country === 'JPN');
    },
    {
      onError: error => {
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(dateRange.from, dateRange.to);
        const errorMessage = getErrorContent(error);
        purchaseCsvDownloadError(
          merchantName,
          selectedVenueIds.length,
          'merchant',
          daysDiff,
          merchantId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
          errorMessage ?? '',
        );
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
      onSuccess: () => {
        const endTime = performance.now();
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(dateRange.from, dateRange.to);

        const merchantId = merchant.itemid.$oid;
        purchaseCsvDownloadView(
          endTime - startTime,
          merchantName,
          selectedVenueIds.length,
          'merchant',
          daysDiff,
          merchantId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
        );
      },
    },
  );

  // Currently, we get timezone from one of the venues under a Merchant,
  // assuming all the venues are under the same timezone.
  const timezone = !!venues && venues?.length > 0 ? venues[0].timezone : '';
  const { downloadPurchaseDataV2, isDownloadingV2 } = useDownloadBrandPurchasedDataV2(
    selectedVenueIds,
    merchantId,
    localize(merchant.name, intl.locale),
    'merchant',
    timezone,
  );

  if (!venues || venues?.length === 0) {
    return (
      <Root>
        <EmptyWrapper>
          <Empty
            title={intl.formatMessage({ id: 'purchases.merchant.empty-title' })}
            description={intl.formatMessage({ id: 'purchases.merchant.empty-description' })}
          />
        </EmptyWrapper>
      </Root>
    );
  }

  return (
    <Root>
      <Sidebar>
        <MenuItemGroup>
          {Object.keys(periodTranslationMap).map((period: PreDefinedPeriod) => (
            <MenuActionItem
              key={period}
              variant="black"
              onClick={() => {
                setDateRangeQuery({ period, from: null, to: null });
              }}
              selected={downloadPeriod === period}
              disabled={isDownloading}
            >
              <FormattedMessage id={periodTranslationMap[period]} />
            </MenuActionItem>
          ))}
        </MenuItemGroup>
        <DateRangeWrapper>
          <DateRangePickerInput
            value={dateRange}
            onChange={dateRange => {
              if (dateRange) {
                setDateRangeQuery({ period: 'custom', ...dateRange });
              }
            }}
            disabled={isDownloading}
            disableClear
            disabledDays={{
              before: DateTime.now().minus({ months: 3 }).plus({ days: 1 }).toJSDate(),
              after: DateTime.now().toJSDate(),
            }}
          />
        </DateRangeWrapper>
      </Sidebar>
      <RightContent>
        <Toolbar>
          <T.Heading6>
            <FormattedMessage id="purchases.merchant.combined-data-header" />
          </T.Heading6>
          <Button
            variant="blue"
            size="small"
            icon={<Download />}
            disabled={isDownloadingV2 || selectedVenueIds.length === 0}
            loading={isDownloadingV2}
            onClick={() => downloadPurchaseDataV2()}
          >
            <FormattedMessage id="purchases.merchant.download-report" values={{ count: selectedVenueIds.length }} />
          </Button>
        </Toolbar>
        <VenuesTable venues={venues} selectedVenueIds={selectedVenueIds} onSelectRows={setSelectedVenueIds} />
      </RightContent>
    </Root>
  );
};

export default MerchantPurchaseData;
